/**
 * Gerar Comissões para Pagamento
 * Relatório para pagar comissões vinculadas por cargo (Corr, Aten, G.Ad, G.Op)
 */
import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  ArrowLeft, Search, Printer, Download, Eye, DollarSign, 
  AlertTriangle, CheckCircle, Users, Building2, FileText
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("pt-BR");
};

export default function GerarComissoesPagamento() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  
  // Filtros
  const [filtroCargo, setFiltroCargo] = useState("corretor");
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("pendente");
  
  // Dialog de detalhes/pagamento
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState(null);
  const [contratosSelecionados, setContratosSelecionados] = useState([]);
  
  // Dialog de relatório
  const [showRelatorio, setShowRelatorio] = useState(false);

  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Verificar perfil do usuário
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const perfilUsuario = (usuario.perfil || "").toUpperCase();
      const isCorretor = perfilUsuario === "CORRETOR";
      // CORRETOR só vê seus próprios contratos, ATENDENTE vê todos
      
      let filtroOperador = "";
      if (isCorretor && usuario.nome) {
        const nomeBusca = usuario.codigo_usuario ? `${usuario.codigo_usuario} ${usuario.nome}`.trim() : usuario.nome;
        filtroOperador = `?operador=${encodeURIComponent(nomeBusca)}`;
      }
      
      const contratosRes = await axios.get(`${API}/contratos${filtroOperador || '?per_page=100'}`);
      const contratosData = contratosRes.data?.contratos || contratosRes.data || [];
      
      // Filtrar apenas contratos com comissão paga (recebida do banco)
      const contratosPagos = contratosData.filter(c => c.comissao_paga);
      setContratos(contratosPagos);
      
      // Extrair colaboradores únicos dos contratos
      extrairColaboradores(contratosPagos);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const extrairColaboradores = (contratosData) => {
    const colaboradoresMap = new Map();
    
    contratosData.forEach(c => {
      // Extrair colaboradores de cada cargo
      const cargos = [
        { cargo: "corretor", nome: c.corretor_nome || c.vendedor || "Corretor Padrão", cpf: c.corretor_cpf },
        { cargo: "atendente", nome: c.atendente_nome || c.operador_comissao || "Atendente Padrão", cpf: c.atendente_cpf },
        { cargo: "ger_adm", nome: c.ger_adm_nome || "Gerente Adm", cpf: c.ger_adm_cpf },
        { cargo: "ger_oper", nome: c.ger_oper_nome || "Gerente Oper", cpf: c.ger_oper_cpf }
      ];
      
      cargos.forEach(({ cargo, nome, cpf }) => {
        if (!nome) return;
        const key = `${cargo}-${nome}`;
        
        if (!colaboradoresMap.has(key)) {
          colaboradoresMap.set(key, {
            id: key,
            cargo,
            nome,
            cpf: cpf || "000.000.000-00",
            banco: c.banco_colaborador || "Banco Brasil",
            agencia: c.agencia_colaborador || "0001",
            conta: c.conta_colaborador || "12345-6",
            contratos: []
          });
        }
        
        colaboradoresMap.get(key).contratos.push(c);
      });
    });
    
    setColaboradores(Array.from(colaboradoresMap.values()));
  };

  // Calcular comissões por colaborador
  const colaboradoresFiltrados = useMemo(() => {
    return colaboradores
      .filter(col => col.cargo === filtroCargo)
      .filter(col => !filtroNome || col.nome.toLowerCase().includes(filtroNome.toLowerCase()))
      .map(col => {
        let totalComissao = 0;
        let totalCreditos = 0;
        let totalDescontos = 0;
        let contratosPendentes = 0;
        let contratosParaPagar = [];
        let contratosBloqueados = [];
        
        col.contratos.forEach(c => {
          const comissaoBase = parseFloat(c.comissao_recebida_valor) || c.comissao_esperada || 0;
          let comissaoCargo = 0;
          
          // Calcular comissão baseada no cargo
          switch(col.cargo) {
            case "corretor":
              comissaoCargo = comissaoBase * ((c.corretor_percentual ?? 30) / 100);
              break;
            case "atendente":
              comissaoCargo = comissaoBase * ((c.atendente_percentual ?? 5) / 100);
              break;
            case "ger_adm":
              comissaoCargo = comissaoBase * ((c.ger_administrativo_percentual ?? 10) / 100);
              break;
            case "ger_oper":
              comissaoCargo = comissaoBase * ((c.ger_operacional_percentual ?? 0) / 100);
              break;
            default:
              comissaoCargo = 0;
          }
          
          // Verificar se já foi pago para este colaborador
          const campoPago = `${col.cargo}_pago`;
          const jaPago = c[campoPago] || false;
          
          // Verificar pendências (contrato físico pendente há mais de 7 dias)
          const dataAprovacao = c.data_aprovacao ? new Date(c.data_aprovacao) : null;
          const diasPendente = dataAprovacao ? Math.floor((new Date() - dataAprovacao) / (1000 * 60 * 60 * 24)) : 0;
          const documentoPendente = !c.documento_fisico_ok && diasPendente > 7;
          
          if (!jaPago && comissaoCargo > 0) {
            totalComissao += comissaoCargo;
            
            if (documentoPendente) {
              contratosBloqueados.push({ ...c, comissaoCargo, diasPendente });
            } else {
              contratosParaPagar.push({ ...c, comissaoCargo });
            }
          }
        });
        
        return {
          ...col,
          totalComissao,
          totalCreditos,
          totalDescontos,
          aPagar: totalComissao + totalCreditos - totalDescontos,
          contratosParaPagar,
          contratosBloqueados,
          temBloqueio: contratosBloqueados.length > 0
        };
      })
      .filter(col => col.totalComissao > 0 || col.contratosBloqueados.length > 0);
  }, [colaboradores, filtroCargo, filtroNome]);

  // Agrupar por banco
  const colaboradoresPorBanco = useMemo(() => {
    const grupos = {};
    
    colaboradoresFiltrados.forEach(col => {
      const banco = col.banco || "Outros";
      if (!grupos[banco]) {
        grupos[banco] = { colaboradores: [], total: 0 };
      }
      grupos[banco].colaboradores.push(col);
      grupos[banco].total += col.aPagar;
    });
    
    return grupos;
  }, [colaboradoresFiltrados]);

  const totalGeral = useMemo(() => {
    return colaboradoresFiltrados.reduce((sum, col) => sum + col.aPagar, 0);
  }, [colaboradoresFiltrados]);

  // Abrir detalhes
  const abrirDetalhes = (colaborador) => {
    setColaboradorSelecionado(colaborador);
    setContratosSelecionados(colaborador.contratosParaPagar.map(c => c.id));
    setShowDetalhes(true);
  };

  // Pagar comissões selecionadas
  const pagarComissoes = async () => {
    if (!colaboradorSelecionado || contratosSelecionados.length === 0) {
      toast.error("Selecione ao menos um contrato");
      return;
    }
    
    try {
      // Marcar contratos como pagos para este cargo
      const campoPago = `${colaboradorSelecionado.cargo}_pago`;
      const campoDataPago = `${colaboradorSelecionado.cargo}_pago_em`;
      
      for (const contratoId of contratosSelecionados) {
        await axios.patch(`${API}/contratos/${contratoId}/comissao`, {
          [campoPago]: true,
          [campoDataPago]: new Date().toISOString()
        });
      }
      
      toast.success(`${contratosSelecionados.length} comissão(ões) marcada(s) como paga(s)`);
      setShowDetalhes(false);
      carregarDados();
    } catch (error) {
      console.error("Erro ao pagar comissões:", error);
      toast.error("Erro ao processar pagamento");
    }
  };

  // Gerar PDF do relatório
  const gerarPDF = () => {
    if (!colaboradorSelecionado) return;
    
    const doc = new jsPDF();
    const col = colaboradorSelecionado;
    
    // Cabeçalho
    doc.setFontSize(18);
    doc.text("Relatório de Comissões", 14, 20);
    
    doc.setFontSize(10);
    doc.text(`StiloCred - ${new Date().toLocaleDateString("pt-BR")}`, 14, 28);
    
    // Identificação
    doc.setFontSize(12);
    doc.text("IDENTIFICAÇÃO", 14, 40);
    doc.setFontSize(10);
    doc.text(`Nome: ${col.nome}`, 14, 48);
    doc.text(`CPF: ${col.cpf}`, 14, 54);
    doc.text(`Cargo: ${col.cargo.toUpperCase().replace("_", " ")}`, 14, 60);
    doc.text(`Banco: ${col.banco} | Ag: ${col.agencia} | Conta: ${col.conta}`, 14, 66);
    
    // Tabela de contratos
    const tableData = col.contratosParaPagar.map(c => [
      c.numero_contrato || c.id?.substring(0, 8),
      c.cliente_nome || c.cliente,
      c.banco || "-",
      c.tipo_operacao || c.produto || "-",
      formatCurrency(c.comissaoCargo)
    ]);
    
    doc.autoTable({
      startY: 75,
      head: [["Contrato", "Cliente", "Banco", "Tipo", "Comissão"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [16, 185, 129] }
    });
    
    // Total
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(`TOTAL A PAGAR: ${formatCurrency(col.aPagar)}`, 14, finalY);
    
    // Salvar
    doc.save(`comissoes_${col.nome.replace(/\s/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF gerado com sucesso!");
  };

  // Imprimir relatório geral
  const imprimirRelatorioGeral = () => {
    const doc = new jsPDF();
    
    // Cabeçalho
    doc.setFontSize(18);
    doc.text("Relatório Geral de Comissões", 14, 20);
    
    doc.setFontSize(10);
    doc.text(`Cargo: ${filtroCargo.toUpperCase().replace("_", " ")}`, 14, 28);
    doc.text(`Data: ${new Date().toLocaleDateString("pt-BR")}`, 14, 34);
    
    let yPos = 45;
    
    // Por banco
    Object.entries(colaboradoresPorBanco).forEach(([banco, grupo]) => {
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(`BANCO: ${banco}`, 14, yPos);
      yPos += 8;
      
      const tableData = grupo.colaboradores.map(col => [
        col.nome,
        col.cpf,
        formatCurrency(col.totalComissao),
        formatCurrency(col.totalCreditos),
        formatCurrency(col.totalDescontos),
        formatCurrency(col.aPagar)
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [["Nome", "CPF", "Total", "Créditos", "Descontos", "A Pagar"]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 5;
      doc.setFont(undefined, "bold");
      doc.text(`TOTAL ${banco}: ${formatCurrency(grupo.total)}`, 14, yPos);
      yPos += 15;
      
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
    });
    
    // Total geral
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(`TOTAL GERAL A PAGAR: ${formatCurrency(totalGeral)}`, 14, yPos + 10);
    
    doc.save(`relatorio_comissoes_${filtroCargo}_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Relatório gerado com sucesso!");
  };

  const cargoLabels = {
    corretor: "Corretor",
    atendente: "Atendente",
    ger_adm: "Gerente Administrativo",
    ger_oper: "Gerente Operacional"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/relatorios")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Gerar Comissões para Pagamento</h1>
                <p className="text-sm text-slate-500">Pague as comissões dos colaboradores</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={imprimirRelatorioGeral}>
                <Printer className="h-4 w-4 mr-2" />
                Imprimir
              </Button>
              <Button variant="outline" onClick={imprimirRelatorioGeral}>
                <Download className="h-4 w-4 mr-2" />
                Exportar PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filtros */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Cargo</label>
                <Select value={filtroCargo} onValueChange={setFiltroCargo}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corretor">Corretor</SelectItem>
                    <SelectItem value="atendente">Atendente</SelectItem>
                    <SelectItem value="ger_adm">Gerente Administrativo</SelectItem>
                    <SelectItem value="ger_oper">Gerente Operacional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Buscar por nome</label>
                <Input 
                  placeholder="Digite o nome..."
                  value={filtroNome}
                  onChange={e => setFiltroNome(e.target.value)}
                />
              </div>
              <Button onClick={carregarDados}>
                <Search className="h-4 w-4 mr-2" />
                Localizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cards de resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Colaboradores</p>
                  <p className="text-2xl font-bold">{colaboradoresFiltrados.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total a Pagar</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalGeral)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <FileText className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Contratos</p>
                  <p className="text-2xl font-bold">{colaboradoresFiltrados.reduce((sum, c) => sum + c.contratosParaPagar.length, 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bloqueados</p>
                  <p className="text-2xl font-bold text-red-600">{colaboradoresFiltrados.filter(c => c.temBloqueio).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela por banco */}
        {Object.entries(colaboradoresPorBanco).map(([banco, grupo]) => (
          <Card key={banco} className="mb-6">
            <CardHeader className="bg-slate-100 py-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-slate-600" />
                <CardTitle className="text-lg">{banco}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-50">
                    <TableHead className="font-bold">Matríc.</TableHead>
                    <TableHead className="font-bold">Nome</TableHead>
                    <TableHead className="font-bold">CPF</TableHead>
                    <TableHead className="font-bold">Agência</TableHead>
                    <TableHead className="font-bold">Conta</TableHead>
                    <TableHead className="font-bold text-right">Total Comissão</TableHead>
                    <TableHead className="font-bold text-right">Créditos</TableHead>
                    <TableHead className="font-bold text-right">Descontos</TableHead>
                    <TableHead className="font-bold text-right">A Pagar</TableHead>
                    <TableHead className="font-bold text-center">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grupo.colaboradores.map((col, idx) => (
                    <React.Fragment key={col.id}>
                      {col.temBloqueio && (
                        <TableRow>
                          <TableCell colSpan={10} className="bg-red-100 text-red-700 font-medium py-2 text-center">
                            <AlertTriangle className="h-4 w-4 inline mr-2" />
                            ATENÇÃO! COMISSÃO BLOQUEADA POR DOCUMENTO PENDENTE HÁ MAIS DE 7 DIAS!
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className={col.temBloqueio ? "bg-red-50" : ""}>
                        <TableCell className="font-mono text-sm">{String(idx + 1).padStart(4, "0")}</TableCell>
                        <TableCell className="font-medium">{col.nome}</TableCell>
                        <TableCell className="text-sm">{col.cpf}</TableCell>
                        <TableCell className="text-sm">{col.agencia}</TableCell>
                        <TableCell className="text-sm">{col.conta}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(col.totalComissao)}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatCurrency(col.totalCreditos)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(col.totalDescontos)}</TableCell>
                        <TableCell className="text-right font-bold text-emerald-700">{formatCurrency(col.aPagar)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex gap-1 justify-center">
                            <Button 
                              size="sm" 
                              variant="link" 
                              className="text-blue-600 h-auto p-0"
                              onClick={() => abrirDetalhes(col)}
                            >
                              Detalhar
                            </Button>
                            <span className="text-slate-300">|</span>
                            <Button 
                              size="sm" 
                              variant="link" 
                              className="text-emerald-600 h-auto p-0"
                              onClick={() => abrirDetalhes(col)}
                              disabled={col.temBloqueio}
                            >
                              Pagar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <div className="bg-slate-100 px-4 py-2 text-right">
                <span className="font-bold">TOTAL BANCO {banco.toUpperCase()} = {formatCurrency(grupo.total)}</span>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Total geral */}
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="py-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-emerald-800">TOTAL A PAGAR</span>
              <span className="text-2xl font-bold text-emerald-700">{formatCurrency(totalGeral)}</span>
            </div>
          </CardContent>
        </Card>

        {colaboradoresFiltrados.length === 0 && (
          <Card className="mt-6">
            <CardContent className="py-12 text-center text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhuma comissão pendente de pagamento para {cargoLabels[filtroCargo]}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog de Detalhes/Pagamento */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Relatório de Comissões - {colaboradorSelecionado?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {colaboradorSelecionado && (
            <div className="space-y-6">
              {/* Identificação */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-bold text-slate-700 mb-2">IDENTIFICAÇÃO</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Nome:</strong> {colaboradorSelecionado.nome}</p>
                  <p><strong>CPF:</strong> {colaboradorSelecionado.cpf}</p>
                  <p><strong>Cargo:</strong> {cargoLabels[colaboradorSelecionado.cargo]}</p>
                  <p><strong>Banco:</strong> {colaboradorSelecionado.banco} | Ag: {colaboradorSelecionado.agencia} | Conta: {colaboradorSelecionado.conta}</p>
                </div>
              </div>

              {/* Contratos para Pagamento */}
              {colaboradorSelecionado.contratosParaPagar.length > 0 && (
                <div>
                  <h3 className="font-bold text-emerald-700 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    CONTRATOS PARA PAGAMENTO
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-emerald-50">
                        <TableHead className="w-10">
                          <Checkbox 
                            checked={contratosSelecionados.length === colaboradorSelecionado.contratosParaPagar.length}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setContratosSelecionados(colaboradorSelecionado.contratosParaPagar.map(c => c.id));
                              } else {
                                setContratosSelecionados([]);
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Contrato</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Banco</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right">Valor Liberado</TableHead>
                        <TableHead className="text-right">Comissão</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {colaboradorSelecionado.contratosParaPagar.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Checkbox 
                              checked={contratosSelecionados.includes(c.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setContratosSelecionados([...contratosSelecionados, c.id]);
                                } else {
                                  setContratosSelecionados(contratosSelecionados.filter(id => id !== c.id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{c.numero_contrato || c.id?.substring(0, 8)}</TableCell>
                          <TableCell>{c.cliente_nome || c.cliente}</TableCell>
                          <TableCell>{c.banco || "-"}</TableCell>
                          <TableCell>{c.tipo_operacao || c.produto || "-"}</TableCell>
                          <TableCell className="text-right">{formatCurrency(c.valor_liberado || c.valor_solicitado)}</TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">{formatCurrency(c.comissaoCargo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="bg-emerald-100 p-3 text-right rounded-b-lg">
                    <span className="font-bold text-emerald-800">
                      TOTAL: {formatCurrency(colaboradorSelecionado.contratosParaPagar.reduce((sum, c) => sum + c.comissaoCargo, 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* Contratos Bloqueados */}
              {colaboradorSelecionado.contratosBloqueados.length > 0 && (
                <div>
                  <h3 className="font-bold text-red-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    CONTRATOS BLOQUEADOS (Documento Pendente +7 dias)
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50">
                        <TableHead>Contrato</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Banco</TableHead>
                        <TableHead>Dias Pendente</TableHead>
                        <TableHead className="text-right">Comissão</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {colaboradorSelecionado.contratosBloqueados.map(c => (
                        <TableRow key={c.id} className="bg-red-50">
                          <TableCell className="font-medium">{c.numero_contrato || c.id?.substring(0, 8)}</TableCell>
                          <TableCell>{c.cliente_nome || c.cliente}</TableCell>
                          <TableCell>{c.banco || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="destructive">{c.diasPendente} dias</Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold text-red-600">{formatCurrency(c.comissaoCargo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="bg-red-100 p-3 text-right rounded-b-lg">
                    <span className="font-bold text-red-800">
                      TOTAL BLOQUEADO: {formatCurrency(colaboradorSelecionado.contratosBloqueados.reduce((sum, c) => sum + c.comissaoCargo, 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* Resumo */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <h3 className="font-bold text-slate-700 mb-3">RESUMO</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total Comissão:</span>
                    <span className="font-bold">{formatCurrency(colaboradorSelecionado.totalComissao)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600">
                    <span>Créditos:</span>
                    <span>{formatCurrency(colaboradorSelecionado.totalCreditos)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Descontos:</span>
                    <span>{formatCurrency(colaboradorSelecionado.totalDescontos)}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">TOTAL A PAGAR:</span>
                    <span className="font-bold text-emerald-700">{formatCurrency(colaboradorSelecionado.aPagar)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDetalhes(false)}>
              Fechar
            </Button>
            <Button variant="outline" onClick={gerarPDF}>
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={pagarComissoes}
              disabled={contratosSelecionados.length === 0}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Pagar Selecionados ({contratosSelecionados.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
